/**
 * V1-DA GPS Position layer — shows the raw V1-DA GPS target position
 * for the selected vehicle as a green marker with deviation line.
 *
 * Follows the V4PositionLayer pattern: admin-toggled, visual-only, with deviation line.
 * Unlike V5PositionLayer, this is a static coordinate lookup (no animation loop needed).
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import type { CircleLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point, LineString } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { CROSSHAIR_ICON } from '../../utils/vehicleIcons';

const V1DA_POSITION_SOURCE_ID = 'v1da-position-source';
const V1DA_BACKGROUND_LAYER_ID = 'v1da-position-background';
const V1DA_ICON_LAYER_ID = 'v1da-position-icon';
const V1DA_LINE_SOURCE_ID = 'v1da-deviation-source';
const V1DA_LINE_LAYER_ID = 'v1da-deviation-line';

interface V1DAPositionFeatureProperties {
  id: string;
  color_bg: string;
}

const EMPTY_GEOJSON: FeatureCollection<Point, V1DAPositionFeatureProperties> = {
  type: 'FeatureCollection',
  features: [],
};

const EMPTY_LINE_GEOJSON: FeatureCollection<LineString> = {
  type: 'FeatureCollection',
  features: [],
};

export const V1DAPositionLayer = memo(function V1DAPositionLayer() {
  // Use specific selector - only re-renders when selected vehicle changes
  const selectedVehicle = useVehicleStore((state) =>
    state.selectedVehicleId ? state.vehicles.get(state.selectedVehicleId) : null
  );
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const showV1DAPosition = useSettingsStore((state) => state.showV1DAPosition);
  const isLoggedIn = useAuthStore((state) => !!state.user);

  // Convert selected vehicle's V1-DA GPS target position to GeoJSON
  const geojson = useMemo(() => {
    if (!selectedVehicle || !showV1DAPosition || !isLoggedIn) {
      return EMPTY_GEOJSON;
    }

    // Skip dead vehicles
    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_GEOJSON;
    }

    // V1-DA GPS position is targetLatitude/targetLongitude (latest received GPS coords)
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [selectedVehicle.targetLongitude, selectedVehicle.targetLatitude],
          },
          properties: {
            id: selectedVehicle.service_journey_id,
            color_bg: selectedVehicle.line?.color_bg || '#666666',
          },
        },
      ],
    } as FeatureCollection<Point, V1DAPositionFeatureProperties>;
  }, [selectedVehicle, showV1DAPosition, isLoggedIn]);

  // Deviation line from current interpolated position to GPS target
  const lineGeojson = useMemo(() => {
    if (!selectedVehicle || !showV1DAPosition || !isLoggedIn) {
      return EMPTY_LINE_GEOJSON;
    }
    if (selectedVehicle.visualState === 'dead') {
      return EMPTY_LINE_GEOJSON;
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [selectedVehicle.longitude, selectedVehicle.latitude],
              [selectedVehicle.targetLongitude, selectedVehicle.targetLatitude],
            ],
          },
          properties: {},
        },
      ],
    } as FeatureCollection<LineString>;
  }, [selectedVehicle, showV1DAPosition, isLoggedIn]);

  // Deviation line style — green dashed
  const lineLayerStyle: LineLayerSpecification = useMemo(
    () => ({
      id: V1DA_LINE_LAYER_ID,
      type: 'line',
      source: V1DA_LINE_SOURCE_ID,
      paint: {
        'line-color': '#22c55e',
        'line-width': 2.5,
        'line-opacity': 0.7,
        'line-dasharray': [4, 3],
      },
    }),
    []
  );

  // V1-DA position background layer — green semi-transparent circle
  const backgroundLayerStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: V1DA_BACKGROUND_LAYER_ID,
      type: 'circle',
      source: V1DA_POSITION_SOURCE_ID,
      paint: {
        'circle-radius': 14,
        'circle-color': ['get', 'color_bg'],
        'circle-stroke-color': '#22c55e',
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 0.6,
        'circle-opacity': 0.3,
      },
    }),
    []
  );

  // V1-DA position icon layer — green crosshair
  const iconLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: V1DA_ICON_LAYER_ID,
      type: 'symbol',
      source: V1DA_POSITION_SOURCE_ID,
      layout: {
        'icon-image': CROSSHAIR_ICON,
        'icon-size': 2.0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
      paint: {
        'icon-color': '#22c55e',
        'icon-opacity': 0.9,
      },
    }),
    []
  );

  // Don't render if toggle is off, no vehicle selected, or not logged in
  if (!showV1DAPosition || !selectedVehicleId || !isLoggedIn) {
    return null;
  }

  return (
    <>
      {/* Deviation line (rendered below marker) */}
      <Source id={V1DA_LINE_SOURCE_ID} type="geojson" data={lineGeojson}>
        <Layer {...lineLayerStyle} />
      </Source>

      {/* V1-DA GPS position marker: background circle + crosshair icon */}
      <Source id={V1DA_POSITION_SOURCE_ID} type="geojson" data={geojson}>
        <Layer {...backgroundLayerStyle} />
        <Layer {...iconLayerStyle} />
      </Source>
    </>
  );
});
